import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum, RumEvent } from '@datadog/browser-rum'
import { Settings } from '@lightningjs/sdk'

import { Flo } from '../types/flo-auth-api'
import { PlayerModel } from '../types/flo-experience'
import { getAuthToken, getUserData } from './authentication'
import { getAppName, getAppVersion } from './info'
import {
  getContinueWatchingApiUrL,
  getCoreApiUrl,
  getEntitlementsUrl,
  getLiveApiUrL,
  getOffersUrl,
  getPaymentsUrl,
} from './network'
import { isStagingEnabled } from './settingsUtils'

export const initDatadog = () => {
  const clientToken = Settings.get('app', 'env.APP_DATADOG_CLIENT_TOKEN', '')
  const rumConfig = getDatadogRumConfig()

  datadogLogs.init({
    clientToken: clientToken,
    env: isStagingEnabled() ? 'stag' : 'prod',
    service: getAppName(),
    version: getAppVersion(),
    forwardErrorsToLogs: false,
    sessionSampleRate: 20,
  })

  if (rumConfig.appId && rumConfig.clientToken && isTizen) {
    const sampleRate = isStagingEnabled() ? 100 : 20
    const tracingUrls = getAllowedTracingUrls()

    datadogRum.init({
      applicationId: rumConfig.appId,
      clientToken: rumConfig.clientToken,
      site: 'datadoghq.com',
      service: getAppName(),
      version: getAppVersion(),
      env: isStagingEnabled() ? 'stag' : 'prod',
      sessionSampleRate: sampleRate,
      sessionReplaySampleRate: sampleRate,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: tracingUrls,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      enablePrivacyForActionName: true,
      sessionPersistence: 'local-storage',
      beforeSend(event) {
        const shouldEventBeDiscard = shouldDiscard(event)
        // true: the event is sent, false: the event is skipped
        return shouldEventBeDiscard
      },
    })
  }

  const token = getAuthToken()

  if (token) {
    setDatadogUser(getUserData())
  }
}

export const getDatadogRumConfig = (): { appId: string; clientToken: string } => {
  let platformKey: string | null = null

  if (isVizio) platformKey = 'VIZIO'
  else if (isTizen) platformKey = 'SAMSUNG'
  else if (isWebos) platformKey = 'LG'

  if (!platformKey) {
    return {
      appId: '',
      clientToken: '',
    }
  }

  const clientToken = Settings.get('app', `env.APP_DATADOG_${platformKey}_RUM_CLIENT_TOKEN`, '')
  const appId = Settings.get('app', `env.APP_DATADOG_${platformKey}_RUM_APP_ID`, '')

  return { appId, clientToken }
}

export const shouldDiscard = (event: RumEvent) => {
  const tracingUrls = getAllowedTracingUrls()

  if (event.type === 'resource') {
    const url = event.resource?.url

    if (!url) return true

    // If the event is resource type we need to filter it out
    // otherwise we are gonna be tracking stuff not related to
    // FloSports main services like Youbora, Segment, Vizbee, etc
    return tracingUrls.some((allowed) => url.startsWith(allowed))
  }

  return true
}

export const getAllowedTracingUrls = () => {
  return [
    getCoreApiUrl(),
    getLiveApiUrL(),
    getEntitlementsUrl(),
    getOffersUrl(),
    getPaymentsUrl(),
    getContinueWatchingApiUrL(),
  ]
}

export const generateUniqueId = () => {
  return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(10)
}

export const setDatadogUser = (data: Flo.Auth.LoginResponse) => {
  const userObj = {
    id: data?.user_id.toString(),
    email: data?.user?.email,
    name: data?.user?.username,
  }

  datadogLogs.setUser(userObj)
  datadogRum.setUser(userObj)
}

export const clearDatadogUser = () => {
  datadogLogs.clearUser()
  datadogRum.clearUser()
}

export const logDatadogError = (messageContext: any, ex: Error) => {
  datadogLogs.logger.error('Error occurred', messageContext, ex)
}

export const logDatadogInfo = (msg: string, data: PlayerModel) => {
  const info = createDatadogErrorObj(data)

  datadogLogs.logger.info(msg, info)
}

export const logGenericDatadogError = (message: string, messageContext: string, ex?: Error) => {
  datadogLogs.logger.error(message, { messageContext }, ex)
}

export const createDatadogErrorObj = (data: PlayerModel) => {
  return {
    ...(data?.analytics?.video_analytics?.event_id && {
      eventId: data.analytics.video_analytics.event_id,
    }),
    ...(data?.analytics?.video_analytics?.event_name && {
      eventTitle: data.analytics.video_analytics.event_name,
    }),
    ...(data?.analytics?.extra_context?.live_id && {
      liveEventId: data?.analytics?.extra_context?.live_id,
    }),
    streamId: data?.content_metadata?.stream_id ?? data?.content_metadata?.node_id,
    streamName: data?.content_metadata?.stream_name ?? data?.content_metadata?.title,
    ...(data?.content_metadata?.playlist && {
      playlistUrl: data?.content_metadata?.playlist,
    }),
  }
}

export const createErrorException = (errorData) => {
  const err = new Error()

  err.name = errorData?.error?.event
  err.message = errorData?.error?.description

  return err
}
